/**
 * UI Utilities for Revstack Widgets
 *
 * This module provides reusable UI utility functions that return JSX elements
 * for common widget components like headers, footers, and other UI parts.
 */

import React, { useState } from "react";
import {
    RevstackFilterTypes,
    TableExportFormats,
    CatAnalyticsSubCatDrillDownContentViews,
    CA_DD_MODULES,
    FilterPosition,
    GraphTypes,
    OptionValues,
    SubModule,
    CommonWidgetMetrics,
    RevstackFrequencySlicerOptions,
} from "@/ts/common-types";
import {
    Input,
    Button,
    Dropdown,
    MenuProps,
    Skeleton,
    Radio,
    Select,
    Checkbox,
} from "antd";
import {
    BsSearch,
    BsEye,
    BsLayoutThreeColumns,
    BsDownload,
    BsFileEarmarkExcel,
    BsFileEarmarkSpreadsheet,
    BsGraphUpArrow,
    BsArrowsExpand,
    BsEyeSlash,
} from "react-icons/bs";
import { MdAutoGraph } from "react-icons/md";
import { PiTreeStructureFill } from "react-icons/pi";
import Title from "antd/es/typography/Title";
import { capitalizeSpecificSubstring, titleCase } from "@/ts/utils";
import ViewAllButtonWidget from "@/components/widgets/ViewAllButtonWidget";
import ComingSoonTooltip from "@/components/widgets/ComingSoonTooltip";
import AiNotes from "@images/ai_notes.png";
import ChannelImage from "@/components/misc/ChannelImage";
import { channelsMapping, htmlLoader, REVSTACK_TRENDS_LIGHT_CHART_COLORS } from "../constants";

interface IRevstackTableHeaderConfig {
    noPadding?: boolean;
    isSearchReq?: boolean;
    data?: any;
    isCompetitionButtonReq?: boolean;
    isLayoutButtonReq?: boolean;
    isDownloadButtonReq?: boolean;
    layoutConfig?: any;
    searchHandler?: (e: React.ChangeEvent<HTMLInputElement>, data: any) => void;
    competitionButtonHandler?: () => void;
    createColumnVisibilityMenu?: () => any;
    downloadButtonHandler?: (format: TableExportFormats) => void;
    isTitleRequired?: boolean;
    isLoading?: boolean;
    type?: "category" | "subcategory" | "regional";
    selectedCardName?: string;
    childFilters?: any[];
    additionalFilters?: any[];
    computedFilterOptions?: any[];
    setSelectedFilter?: (value: string) => void;
    searchValue?: string;
    isExpandRowsButtonReq?: boolean;
    hideCompetition?: boolean;
    expandRowsButtonHandler?: () => void;
    // Column visibility props
    columnVisibilityMap?: Record<string, boolean>;
    onColumnToggle?: (columnKey: string) => void;
    justifyContent?: "justify-content-start" | "justify-content-end" ;
}

// Widget Card Filters Configuration Types
interface FilterConfig {
    section: CA_DD_MODULES | "";
    type: RevstackFilterTypes;
    disabled?: boolean;
    isComingSoon?: boolean;
    props?: Record<string, any>;
    render?: (props: any) => React.ReactNode;
}

interface RadioFilterConfig {
    options: Array<{
        value: string;
        label: string;
        count?: number;
        disabled?: boolean;
    }>;
    defaultValue?: string;
    onChange?: (value: string) => void;
    disabled?: boolean;
}

interface DropdownRadioFilterConfig {
    options: Array<{
        value: string;
        label: string;
        disabled?: boolean;
    }>;
    defaultValue?: string;
    onChange?: (value: string) => void;
    dropdownButtonText?: string;
    submitBtnText?: string;
    cancelBtnText?: string;
    onCancel?: () => void;
    onSubmit?: (selectedValue: string) => void;
    placement?: "top" | "bottom" | "bottomRight" | "bottomLeft" | "topRight" | "topLeft";
    disabled?: boolean;
}

interface SelectFilterConfig {
    options: Array<{
        value: string;
        label: string;
    }>;
    defaultValue?: string;
    width?: number;
    onChange?: (val: any) => void;
    mode?: "multiple" | "tags";
    value?: any;
    placeholder?: string;
    maxTagCount?: number;
    maxTagTextLength?: number;
    tagRender?: (props: any) => React.ReactElement;
    dropdownRender?: (menu: React.ReactElement) => React.ReactElement;
    customDisplayText?: string;
    isCustomDisplayMode?: boolean;
    disabled?: boolean;
    styles?: any;
}

interface MultiSelectFilterConfig {
    options: Array<{
        name: string;
        logo_url: string;
        value: string;
        label: string;
    }>;
    value?: string[];
    width?: number;
    onChange?: (value: string[]) => void;
    onOpenChange?: (open: boolean) => void;
    placeholder?: string;
    showAllOption?: boolean;
    allOptionValue?: string;
    allOptionLabel?: string;
    maxDisplayItems?: number;
    showOnlyAllLabelWhenAllSelected?: boolean;
    useTags?: boolean;
}

interface ChartTypeFilterConfig {
    options: Array<{
        value: string;
        label: string;
        icon: React.ReactNode;
        disabled?: boolean;
        isComingSoon?: boolean;
    }>;
    value?: GraphTypes | CatAnalyticsSubCatDrillDownContentViews;
    onChange?: (value: GraphTypes) => void;
}

interface IWidgetCardFiltersConfig {
    filters: FilterConfig[];
    positionFilters?: FilterPosition;
}

/**
 * Builds a reusable table header component with filters, search, and action buttons
 *
 * @param config - Configuration object for the table header
 * @returns JSX element for the table header
 */
export const buildRevstackTableHeader = (
    config: IRevstackTableHeaderConfig = {}
) => {
    const {
        noPadding = false,
        isSearchReq = false,
        isCompetitionButtonReq = false,
        isLayoutButtonReq = false,
        isDownloadButtonReq = false,
        isExpandRowsButtonReq = false,
        expandRowsButtonHandler = () => { },
        data = null,
        isTitleRequired = false,
        isLoading = false,
        type = "category",
        selectedCardName = "",
        childFilters = [],
        additionalFilters = [],
        computedFilterOptions = [],
        setSelectedFilter = () => { },
        searchHandler = () => { },
        competitionButtonHandler = () => { },
        hideCompetition = false,
        createColumnVisibilityMenu = () => { },
        downloadButtonHandler = () => { },
        searchValue = "",
        columnVisibilityMap = {},
        onColumnToggle = () => { },
        justifyContent = null,
    } = config;


    const showActionButtons =
        isCompetitionButtonReq || isLayoutButtonReq || isDownloadButtonReq;
    const showComponent =
        isTitleRequired ||
        childFilters?.length > 0 ||
        additionalFilters?.length > 0 ||
        isSearchReq ||
        showActionButtons;


    return showComponent ? (
        <div className={`revstack-table-header ${noPadding ? "" : "pb-2"} d-flex ${justifyContent ? justifyContent : ""} `}>
            {isTitleRequired &&
                (isLoading ? (
                    <Skeleton.Node
                        className="w-50"
                        active
                        style={{ height: 40 }}
                    />
                ) : (
                    <Title
                        level={5}
                        style={{
                            fontSize: 18,
                            fontWeight: 600,
                            margin: 0,
                        }}
                    >
                        {/* TODO: move this hardcoding to config where isTitleRequired is true like titlePrefix */}
                        {type === "category" ? (
                            <span style={{ color: "#1E1B39" }}>SKUs - </span>
                        ) : (
                            ""
                        )}
                        <span
                            style={{
                                color:
                                    type === "category" ? "#919191" : "#1E1B39",
                            }}
                        >
                            {titleCase(selectedCardName)}
                        </span>
                    </Title>
                ))}

            {(childFilters && childFilters.length > 0) ||
                computedFilterOptions.length > 0 ? (
                isLoading ? (
                    <Skeleton.Node active style={{ height: 40, width: 100 }} />
                ) : (
                    <div
                        className="d-flex align-items-center justify-content-between gap-3"
                        style={{
                            width: "fit-content",
                        }}
                    >
                        {/* Render child filters if they exist */}
                        {childFilters && childFilters.length > 0
                            ? childFilters.map((filter: any, index: number) => (
                                <div key={index}>
                                    {buildWidgetCardFilters({
                                        filters: [
                                            {
                                                section: "",
                                                type: filter.type,
                                                props: {
                                                    options:
                                                        computedFilterOptions,
                                                    onChange: (
                                                        value: string
                                                    ) =>
                                                        setSelectedFilter(
                                                            value
                                                        ),
                                                    defaultValue:
                                                        filter.defaultFilter,
                                                    width:
                                                        filter.type ===
                                                            "SELECT"
                                                            ? 150
                                                            : undefined,
                                                },
                                            },
                                        ],
                                    })}
                                </div>
                            ))
                            : childFilters?.[0]
                                        ? /* Render primary filter */
                                        buildWidgetCardFilters({
                                            filters: [
                                                {
                                                    section: "",
                                                    type: (childFilters[0].type ===
                                                        "SELECT"
                                                        ? RevstackFilterTypes.SELECT
                                                        : RevstackFilterTypes.RADIO) as RevstackFilterTypes,
                                                    props: {
                                                        options: computedFilterOptions,
                                                        onChange: (value: string) =>
                                                            setSelectedFilter(value),
                                                        defaultValue:
                                                            childFilters[0].defaultFilter,
                                                        width:
                                                            childFilters[0].type ===
                                                                "SELECT"
                                                                ? 150
                                                                : undefined,
                                                    },
                                                },
                                            ],
                                        })
                                        : null}
                        <ViewAllButtonWidget />
                    </div>
                )
            ) : null}
            {isSearchReq && (
                <Input
                    className="search-input"
                    placeholder="Search..."
                    variant="outlined"
                    suffix={<BsSearch />}
                    value={searchValue}
                    onChange={(e) => searchHandler?.(e, data?.tableData)}
                />
            )}
            {additionalFilters?.length > 0 || showActionButtons ? (
                <div className="d-flex align-items-center gap-3 justify-content-end">
                    {additionalFilters?.length > 0 &&
                        buildWidgetCardFilters({
                            filters: additionalFilters?.map((filter) => ({
                                ...filter,
                                props: {
                                    ...filter.props,
                                    onChange:
                                        filter.props?.onChange ||
                                        ((value: string) =>
                                            setSelectedFilter(value)),
                                },
                            })),
                        })}
                    {showActionButtons && (
                        <div className="d-flex align-items-center justify-content-end gap-2">
                            {isCompetitionButtonReq && (
                                <Button
                                    className={`competition-btn ${!hideCompetition ? "toggled" : ""
                                        }`}
                                    onClick={competitionButtonHandler}
                                    icon={
                                        hideCompetition ? (
                                            <BsEye
                                                className="d-flex align-items-center justify-content-center"
                                                size={15.95}
                                            />
                                        ) : (
                                            <BsEyeSlash
                                                className="d-flex align-items-center justify-content-center"
                                                size={15.95}
                                            />
                                        )
                                    }
                                >
                                    <span className="fs-7 fw-600">
                                        {hideCompetition
                                            ? "Show Competition"
                                            : "Hide Competition"}
                                    </span>
                                </Button>
                            )}
                            {isLayoutButtonReq && (
                                <Dropdown
                                    dropdownRender={() => {
                                        const menuItems =
                                            createColumnVisibilityMenu();
                                        return (
                                            <div className="revstack-multi-select-checkbox bg-white shadow-sm">
                                                {/* <div
                                        className="dropdown-item"
                                        role="button"
                                        // onClick={handleAllChannelsToggle}
                                    >
                                        <Checkbox checked={false} />
                                        <div className="item-label d-flex align-items-center gap-2">
                                            All Channels
                                        </div>
                                    </div> */}
                                            {menuItems?.map((item: any) => {
                                                const isChecked =
                                                    columnVisibilityMap[
                                                        item.key
                                                    ] !== false;
                                                return (
                                                    <div
                                                        key={item.key}
                                                        className="dropdown-item"
                                                        role="button"
                                                        onClick={() =>
                                                            onColumnToggle(
                                                                item.key
                                                            )
                                                        }
                                                    >
                                                        <Checkbox
                                                            checked={isChecked}
                                                            onChange={(e) =>
                                                                e.preventDefault()
                                                            }
                                                        />
                                                        <div className="item-label d-flex align-items-center gap-2">
                                                            {titleCase(
                                                                item.label
                                                            )}
                                                        </div>
                                                    </div>
                                                );
                                            })}
                                            </div>
                                        );
                                    }}
                                    trigger={["click"]}
                                    placement="bottomRight"
                                >
                                    <div className="table-header-action-btn">
                                        <BsLayoutThreeColumns />
                                    </div>
                                </Dropdown>
                            )}
                            {isExpandRowsButtonReq && (
                                <div
                                    className="table-header-action-btn"
                                    onClick={expandRowsButtonHandler}
                                >
                                    <BsArrowsExpand />
                                </div>
                            )}
                            {isDownloadButtonReq && downloadButtonHandler && (
                                <Dropdown
                                    overlayClassName="revstack-dashboard-popup"
                                    menu={{
                                        items: [
                                            {
                                                key: TableExportFormats.CSV,
                                                className:
                                                    "drill-down-table-menu-item",
                                                label: "Export as CSV",
                                                icon: (
                                                    <BsFileEarmarkExcel size={12} />
                                                ),
                                                onClick: () =>
                                                    downloadButtonHandler(
                                                        TableExportFormats.CSV
                                                    ),
                                            },
                                            {
                                                key: TableExportFormats.EXCEL,
                                                className:
                                                    "drill-down-table-menu-item",
                                                label: "Export as Excel",
                                                icon: (
                                                    <BsFileEarmarkSpreadsheet
                                                        size={12}
                                                    />
                                                ),
                                                onClick: () =>
                                                    downloadButtonHandler(
                                                        TableExportFormats.EXCEL
                                                    ),
                                            },
                                        ] as MenuProps["items"],
                                        style: {
                                            maxHeight: 350,
                                            borderRadius: 6,
                                        },
                                    }}
                                    trigger={["click"]}
                                    placement="bottomRight"
                                >
                                    <div className="table-header-action-btn">
                                        <BsDownload />
                                    </div>
                                </Dropdown>
                            )}
                        </div>)}
                </div>
            ) : null}
        </div>
    ) : null;
};
const DropdownRadioFilter: React.FC<{ config: DropdownRadioFilterConfig }> = ({ config }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [selectedValue, setSelectedValue] = useState(
        config.defaultValue || config.options?.[0]?.value
    );

    const handleSubmit = () => {
        config.onSubmit?.(selectedValue);
        setIsOpen(false);
    };

    return (
        <Dropdown
            open={isOpen}
            onOpenChange={setIsOpen}
            trigger={["click"]}
            placement={config.placement || "bottomRight"}
            disabled={config.disabled || false}
            dropdownRender={() => {
                return (
                    <div className="dropdown-radio-group-container">
                        <Radio.Group
                            value={selectedValue}
                            onChange={(e) => {
                                const newValue = e.target.value;
                                setSelectedValue(newValue);
                                config.onChange?.(newValue);
                            }}
                            options={config.options}
                            style={{
                                display: "flex",
                                flexDirection: "column",
                                gap: 8,
                            }}
                        />
                        <Button
                            type="primary"
                            onClick={handleSubmit}
                        >
                            {config.submitBtnText || "Submit"}
                        </Button>
                    </div>
                );
            }}
        >
            <Button type="primary">
                {config.dropdownButtonText || "Open Dropdown"}
            </Button>
        </Dropdown>
    );
};

/**
 * Builds widget card filters with various filter types (radio, select, buttons, etc.)
 *
 * @param config - Configuration object for the widget card filters
 * @returns JSX element for the widget card filters
 */
export const buildWidgetCardFilters = (config: IWidgetCardFiltersConfig) => {
    const { filters = [], positionFilters = FilterPosition.END } = config;

    const renderFilter = (filter: FilterConfig, index: number) => {
        switch (filter.type) {
            case RevstackFilterTypes.BEACON: {
                const beaconButton = (
                    <Button
                        className="beacon-btn"
                        onClick={filter.props?.onClick}
                        disabled={filter.disabled || filter.isComingSoon}
                    >
                        <img src={AiNotes} alt="AiNotes" />
                        Beacon
                    </Button>
                );

                return filter.isComingSoon ? (
                    <ComingSoonTooltip key={`beacon-${index}`}>
                        {beaconButton}
                    </ComingSoonTooltip>
                ) : (
                    <div key={`beacon-${index}`}>{beaconButton}</div>
                );
            }

            case RevstackFilterTypes.TRENDS: {
                const trendsButton = (
                    <Button
                        className="revstack-trends-btn"
                        icon={<BsGraphUpArrow />}
                        onClick={filter.props?.onClick}
                        disabled={filter.disabled || filter.isComingSoon}
                    >
                        Trends
                    </Button>
                );

                return filter.isComingSoon ? (
                    <ComingSoonTooltip key={`trends-${index}`}>
                        {trendsButton}
                    </ComingSoonTooltip>
                ) : (
                    <div key={`trends-${index}`}>{trendsButton}</div>
                );
            }

            case RevstackFilterTypes.RADIO: {
                const radioConfig = filter.props as RadioFilterConfig;
                return (
                    <Radio.Group
                        key={`radio-${index}`}
                        defaultValue={
                            radioConfig.defaultValue ||
                            radioConfig.options?.[0]?.value
                        }
                        buttonStyle="solid"
                        className="revstack-filter-radio-group d-flex d-flex align-items-center justify-content-between"
                        onChange={(e) => radioConfig.onChange?.(e.target.value)}
                        disabled={filter.disabled || filter.isComingSoon}
                    >
                        {radioConfig.options?.map((option: any) => (
                            <Radio.Button
                                key={option.value}
                                className="d-flex align-items-center justify-content-center"
                                value={option.value}
                                disabled={option.disabled}
                            >
                                <span
                                    style={{
                                        display: "inline-flex",
                                        alignItems: "center",
                                        gap: "8px",
                                    }}
                                >
                                    <span>{option.label}</span>
                                    {option.count !== undefined &&
                                        option.count !== null && (
                                            <span
                                                className="badge"
                                                style={{
                                                    backgroundColor: "#E5F8FF",
                                                    color: "#1A1F36",
                                                    fontSize: 10,
                                                    fontWeight: 600,
                                                }}
                                            >
                                                {option.count}
                                            </span>
                                        )}
                                </span>
                            </Radio.Button>
                        ))}
                    </Radio.Group>
                );
            }
            case RevstackFilterTypes.DROPDOWN_RADIO: {
                const dropdownRadioConfig =
                    filter.props as DropdownRadioFilterConfig;
                return <DropdownRadioFilter config={dropdownRadioConfig} />;
            }
            case RevstackFilterTypes.SELECT: {
                const selectConfig = filter.props as SelectFilterConfig;

                if (selectConfig.isCustomDisplayMode) {
                    return (
                        <div
                            key={`select-${index}`}
                            style={{
                                position: "relative",
                                width: selectConfig.width || 120,
                            }}
                        >
                            <Select
                                key={`select-custom-${index}`}
                                mode={selectConfig.mode}
                                defaultValue={
                                    selectConfig.defaultValue ||
                                    selectConfig.options?.[0]?.value
                                }
                                value={selectConfig.value}
                                style={{
                                    width: "100%",
                                    ...selectConfig?.styles,
                                }}
                                options={selectConfig.options}
                                disabled={
                                    filter.disabled || filter.isComingSoon
                                }
                                onChange={selectConfig.onChange}
                                placeholder={selectConfig.placeholder}
                                maxTagCount={selectConfig.maxTagCount}
                                maxTagTextLength={selectConfig.maxTagTextLength}
                                tagRender={selectConfig.tagRender}
                                dropdownRender={selectConfig.dropdownRender}
                            />
                            <div
                                className="position-absolute text-nowrap overflow-hidden pe-1 bg-white"
                                style={{
                                    left: "12px",
                                    right: "24px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    color:
                                        selectConfig.customDisplayText &&
                                            selectConfig.customDisplayText !==
                                            selectConfig.placeholder
                                            ? "#000"
                                            : "#999",
                                    textOverflow: "ellipsis",
                                    pointerEvents: "none",
                                    zIndex: 1,
                                }}
                            >
                                {selectConfig.customDisplayText ||
                                    selectConfig.placeholder}
                            </div>
                        </div>
                    );
                }

                return (
                    <Select
                        key={`select-${index}`}
                        mode={selectConfig.mode}
                        defaultValue={
                            selectConfig.defaultValue ||
                            selectConfig.options?.[0]?.value
                        }
                        value={selectConfig.value}
                        style={{ width: selectConfig.width || 120 }}
                        options={selectConfig.options}
                        disabled={filter.disabled || filter.isComingSoon}
                        onChange={selectConfig.onChange}
                        placeholder={selectConfig.placeholder}
                        maxTagCount={selectConfig.maxTagCount}
                        maxTagTextLength={selectConfig.maxTagTextLength}
                        tagRender={selectConfig.tagRender}
                        dropdownRender={selectConfig.dropdownRender}
                        styles={selectConfig.styles}
                    />
                );
            }

            case RevstackFilterTypes.MULTI_SELECT: {
                const multiSelectConfig =
                    filter.props as MultiSelectFilterConfig;
                const {
                    options = [],
                    value = [],
                    width = 180,
                    onChange = () => { },
                    onOpenChange = () => { },
                    placeholder = "Select Options",
                    showAllOption = true,
                    allOptionValue = OptionValues.ALL,
                    allOptionLabel = "All",
                    maxDisplayItems = 2,
                    showOnlyAllLabelWhenAllSelected = false,
                    useTags = false,
                } = multiSelectConfig;

                // Create options with "All" option if enabled
                const allOptions = showAllOption
                    ? [
                        { value: allOptionValue, label: allOptionLabel },
                        ...options,
                    ]
                    : options;

                // Custom display text logic
                const getDisplayText = () => {
                    if (value.length === 0) {
                        return placeholder;
                    }

                    let displayLabels: string[] = [];

                    if (value.includes(allOptionValue)) {
                        if (showOnlyAllLabelWhenAllSelected) {
                            displayLabels = [allOptionLabel];
                        } else {
                            displayLabels = options.map(
                                (option) => option.label
                            );
                        }
                    } else {
                        displayLabels = value.map((val) => {
                            const option = options.find(
                                (opt) => opt.value === val
                            );
                            return option?.label || val;
                        });
                    }

                    displayLabels = displayLabels.map((label) =>
                        titleCase(label)
                    );

                    if (displayLabels.length <= maxDisplayItems) {
                        return displayLabels.join(", ");
                    } else {
                        return `${displayLabels
                            .slice(0, maxDisplayItems)
                            .join(", ")} +${displayLabels.length - maxDisplayItems
                            }`;
                    }
                };

                return (
                    <div
                        key={`multiSelect-${index}`}
                        style={{ position: "relative", width }}
                    >
                        <Select
                            mode="multiple"
                            value={value}
                            style={{ width: "100%" }}
                            options={allOptions}
                            disabled={filter.disabled || filter.isComingSoon}
                            onChange={(value) => {
                                onChange(value);
                            }}
                            onOpenChange={onOpenChange}
                            placeholder={placeholder}
                            tagRender={
                                useTags
                                    ? (tagProps) => {
                                        const {
                                            value: tagValue,
                                            closable,
                                            onClose,
                                        } = tagProps;
                                        const tagIndex =
                                            value.indexOf(tagValue);

                                        if (tagIndex === 0) {
                                            // Find the label for this value
                                            const option = allOptions.find(
                                                (opt) =>
                                                    opt.value === tagValue
                                            );
                                            const label = titleCase(
                                                option?.label || tagValue
                                            );
                                            const displayLabel =
                                                label.length > 15
                                                    ? label.substring(0, 15) +
                                                    "..."
                                                    : label;

                                            // Show first tag normally
                                            return (
                                                <span
                                                    className="revstack-multi-select-tag"
                                                    key={tagValue}
                                                >
                                                    {displayLabel}
                                                </span>
                                            );
                                        } else if (tagIndex === 1) {
                                            // Show count for remaining tags
                                            const remainingCount =
                                                value.length - 1;
                                            return (
                                                <span
                                                    className="revstack-multi-select-tag"
                                                    key="count"
                                                >
                                                    +{remainingCount}
                                                </span>
                                            );
                                        }
                                        // Hide other tags
                                        return null;
                                    }
                                    : () => null
                            }
                            dropdownClassName="revstack-multi-select-checkbox"
                            dropdownRender={(menu) => (
                                <div className="revstack-multi-select-checkbox">
                                    {/* Sort options to put "All" first */}
                                    {[...allOptions]
                                        .sort((a, b) => {
                                            if (a.value === allOptionValue)
                                                return -1;
                                            if (b.value === allOptionValue)
                                                return 1;
                                            return 0;
                                        })
                                        .map((option) => {
                                            const isChecked = value.includes(
                                                option.value
                                            );
                                            return (
                                                <div
                                                    key={option.value}
                                                    className="dropdown-item"
                                                    role="button"
                                                    onClick={() => {
                                                        const currentValues = [
                                                            ...value,
                                                        ];
                                                        if (
                                                            option.value ===
                                                            allOptionValue
                                                        ) {
                                                            if (
                                                                currentValues.includes(
                                                                    allOptionValue
                                                                )
                                                            ) {
                                                                const newSelection =
                                                                    currentValues.filter(
                                                                        (v) =>
                                                                            v !==
                                                                            allOptionValue
                                                                    );
                                                                onChange(
                                                                    newSelection.length >
                                                                        0
                                                                        ? newSelection
                                                                        : [
                                                                            allOptionValue,
                                                                        ]
                                                                );
                                                            } else {
                                                                onChange([
                                                                    allOptionValue,
                                                                ]);
                                                            }
                                                        } else {
                                                            const isSelected =
                                                                currentValues.includes(
                                                                    option.value
                                                                );
                                                            let newValues;
                                                            if (isSelected) {
                                                                newValues =
                                                                    currentValues.filter(
                                                                        (v) =>
                                                                            v !==
                                                                            option.value
                                                                    );
                                                            } else {
                                                                newValues = [
                                                                    ...currentValues.filter(
                                                                        (v) =>
                                                                            v !==
                                                                            allOptionValue
                                                                    ),
                                                                    option.value,
                                                                ];
                                                            }
                                                            onChange(
                                                                newValues.length >
                                                                    0
                                                                    ? newValues
                                                                    : [
                                                                        allOptionValue,
                                                                    ]
                                                            );
                                                        }
                                                    }}
                                                >
                                                    <Checkbox
                                                        checked={isChecked}
                                                    />
                                                    <div className="item-label d-flex align-items-center gap-2">
                                                        {"logo_url" in
                                                            option && (
                                                                <ChannelImage
                                                                    channel={option}
                                                                    size="small"
                                                                />
                                                            )}
                                                        {titleCase(
                                                            option.label
                                                        )}
                                                    </div>
                                                </div>
                                            );
                                        })}
                                </div>
                            )}
                        />
                        {!useTags && (
                            <div
                                className="position-absolute text-nowrap overflow-hidden pe-1 bg-white"
                                style={{
                                    left: "12px",
                                    right: "24px",
                                    top: "50%",
                                    transform: "translateY(-50%)",
                                    color:
                                        getDisplayText() !== placeholder
                                            ? "#000"
                                            : "#999",
                                    textOverflow: "ellipsis",
                                    pointerEvents: "none",
                                    zIndex: 1,
                                }}
                            >
                                {getDisplayText()}
                            </div>
                        )}
                    </div>
                );
            }

            case RevstackFilterTypes.CHART_TYPE: {
                const chartConfig = filter.props as ChartTypeFilterConfig;
                return (
                    <Radio.Group
                        key={`chartType-${index}`}
                        value={chartConfig.value}
                        onChange={(e) => chartConfig.onChange?.(e.target.value)}
                        buttonStyle="solid"
                        className="revstack-filter-radio-group"
                        disabled={filter.disabled || filter.isComingSoon}
                    >
                        {chartConfig.options?.map((option: any) => {
                            const button = (
                                <Radio.Button
                                    value={option.value}
                                    className="px-2"
                                    key={option.value}
                                    disabled={option.disabled}
                                >
                                    {option.icon} {option.label || ""}
                                </Radio.Button>
                            );

                            return option.isComingSoon ? (
                                <ComingSoonTooltip key={option.value}>
                                    {button}
                                </ComingSoonTooltip>
                            ) : (
                                button
                            );
                        })}
                    </Radio.Group>
                );
            }

            case RevstackFilterTypes.SHOW_TRENDS: {
                const showTrendsButton = (
                    <Button
                        className="d-flex align-items-center justify-content-center"
                        style={{
                            width: "35px",
                            gap: "10px",
                            flexShrink: 0,
                            padding: 0,
                            lineHeight: 1,
                        }}
                        icon={
                            <div className="d-flex align-items-center justify-content-center">
                                <MdAutoGraph size={20} />
                            </div>
                        }
                        onClick={filter.props?.onClick}
                        disabled={filter.disabled || filter.isComingSoon}
                    />
                );

                return filter.isComingSoon ? (
                    <ComingSoonTooltip key={`showTrends-${index}`}>
                        {showTrendsButton}
                    </ComingSoonTooltip>
                ) : (
                    <div key={`showTrends-${index}`}>{showTrendsButton}</div>
                );
            }

            case RevstackFilterTypes.DRILL_DOWN: {
                const drillDownButton = (
                    <Button
                        className="drill-down-btn"
                        onClick={filter.props?.onClick}
                        disabled={filter.disabled || filter.isComingSoon}
                    >
                        Drill Down
                    </Button>
                );

                return filter.isComingSoon ? (
                    <ComingSoonTooltip key={`drillDown-${index}`}>
                        {drillDownButton}
                    </ComingSoonTooltip>
                ) : (
                    <div key={`drillDown-${index}`}>{drillDownButton}</div>
                );
            }

            case RevstackFilterTypes.DEEP_DIVE: {
                const deepDiveButton = (
                    <Button
                        className="deep-dive-btn px-1"
                        onClick={filter.props?.onClick}
                        disabled={filter.disabled || filter.isComingSoon}
                        icon={
                            <PiTreeStructureFill
                                size={16}
                                style={{ transform: "rotate(90deg)" }}
                            />
                        }
                    >
                        Deep Dive
                    </Button>
                );

                return filter.isComingSoon ? (
                    <ComingSoonTooltip key={`deepDive-${index}`}>
                        {deepDiveButton}
                    </ComingSoonTooltip>
                ) : (
                    <div key={`deepDive-${index}`}>{deepDiveButton}</div>
                );
            }

            default:
                return null;
        }
    };

    return (
        <div
            className={`d-flex ${positionFilters === FilterPosition.START
                    ? "justify-content-start"
                    : "justify-content-end"
            } 
            align-items-center gap-3`}
            style={{
                height: 35,
                borderRadius: 6,
            }}
        >
            {filters.map((filter, index) => renderFilter(filter, index))}
        </div>
    );
};

export const showBetaTag = (title: string) => {
    return [titleCase(SubModule.PRODUCT_ANALYSIS)].includes(title);
};

export const productAnalysisMultiRowTooltipConfig = (
    nameField: string | string[] = 'name', 
    selectedReplacement?: string,
    unit?: string
) => {
    const nameFields = Array.isArray(nameField) ? nameField : [nameField];

    return {
        // Keep shared=false so hovering one series doesn't highlight others
        shared: false,
        useHTML: true,
        backgroundColor: "1E1B39",
        borderRadius: 12,
        borderWidth: 0,
        padding: 10,
        formatter: function (
            this: Highcharts.TooltipFormatterContextObject
        ): string {
            const date = this.x !== undefined ? this.x : "";
            // Build rows from all visible series using the hovered point index
            const pointIndex = (this as any).point
                ? (this as any).point.index
                : 0;
            const chart = (this.series as any).chart;
            let chartSeries = chart?.series || [];

            chartSeries = chartSeries.filter((s: any) => s && s.visible);

            const rows = chartSeries
                .map((s: any) => {
                    const y = Array.isArray(s.yData)
                        ? s.yData[pointIndex]
                        : undefined;
                    if (y === null || y === undefined) return null;

                    const color = s.color || "#009FE3";

                    let name = "";
                    if (s.userOptions) {
                        for (const field of nameFields) {
                            if (s.userOptions[field]) {
                                name = s.userOptions[field];
                                break;
                            }
                        }
                    }

                    if (name && selectedReplacement && name.toLocaleLowerCase().includes("selected")) {
                        name = `${selectedReplacement} (selected)`;
                    }

                    // Fallback to s.name if no field found
                    if (!name) {
                        name = s.name || "";
                    }

                    const value =
                        typeof y === "number"
                            ? `${y.toFixed(1)} ${unit}`
                            : "-";
                    return `
                        <div style="display:flex;align-items:center;justify-content:space-between;margin:4px 0;min-width:180px;">
                            <div style="display:flex;align-items:center;gap:8px;color:#fff;font-size:11px;font-weight:500;max-width:140px;overflow:hidden;">
                                <span style="display:inline-block;width:8px;height:8px;border-radius:50%;background:${color};flex-shrink:0;"></span>
                                <span style="white-space:nowrap;overflow:hidden;text-overflow:ellipsis;">${titleCase(String(name))}</span>
                            </div>
                            <div style="color:#fff;font-size:11px;font-weight:600;">${value}</div>
                        </div>
                    `;
                })
                .filter(Boolean)
                .join("");

            return `
                <div style="text-align:left;">
                    ${
                rows ||
                '<div style="color:#ccc;font-size:10px;">No data</div>'
                    }
                </div>
            `;
        }
    }
}

export function skuTooltipFormatter(
    this: Highcharts.TooltipFormatterContextObject,
    currencySymbol: string
): string {
    const chart = (this as any).points?.[0]?.series?.chart;
    if (!chart) return "";

    // Get all points at the current x position across all series
    const currentX = (this as any).points?.[0]?.point?.x;
    const allPoints: any[] = [];

    if (currentX !== undefined && chart.series) {
        chart.series.forEach((series: any) => {
            if (
                series.visible &&
                series.points &&
                series.points[currentX]
            ) {
                allPoints.push(series.points[currentX]);
            }
        });
    }

    // Fallback to this.points if we couldn't get all points
    const points =
        allPoints.length > 0 ? allPoints : (this as any).points || [];

    const tooltipDataArray = chart.userOptions.tooltipData || [];
    const tooltipData = tooltipDataArray[0] || {};
    const tooltipMetrics = chart.userOptions.tooltipMetrics || [];
    const yAxisUnit = chart.userOptions.yAxisUnit || "%";

    const hasData = tooltipData.sku && tooltipData.sku_title;
    let html = `<div style="padding: ${hasData ? "16px" : "0"
        }; border-radius: 12px; background: #1E1B39; max-width: 218px; max-heigth:198px; box-shadow: 0 4px 12px rgba(0,0,0,0.3);">`;

    // Header with product image and title
    if (hasData) {
        html += `
            <div style="display: flex; align-items: flex-start; margin-bottom: 12px; gap: 12px;">
                <div style="flex-shrink: 0; width: 48px; height: 60px; background: white; border-radius: 8px; display: flex; align-items: center; justify-content: center; overflow: hidden;">
                    ${
            tooltipData.thumbnail
                ? `<img src="${tooltipData.thumbnail}" alt="${tooltipData.sku_title}" style="width: 100%; height: 100%; object-fit: contain;" />`
                : ""
                    }
                </div>
                <div style="flex: 1; min-width: 0;">
                    <div style="color: #00A8E8; font-size: 12px; font-weight: 600; line-height: 1.3; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; margin-bottom: 4px; text-align: left;">${
            tooltipData.sku
                    }</div>
                    <div style="color: #FFF; font-size: 12px; font-weight: 600; line-height: 1.3; overflow: hidden; text-overflow: ellipsis; display: -webkit-box; -webkit-line-clamp: 2; -webkit-box-orient: vertical; margin-bottom: 8px;">${
            tooltipData.sku_title
            }</div>`;
        html += `
                    <div style="display: flex; gap: 8px; flex-wrap: wrap;">`;

        points.forEach((point: any) => {
            html += `
                        <div style="display: flex; align-items: center; gap: 6px; min-width: 0;">
                            <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${
                point.color
                            }; flex-shrink: 0;"></span>
                            <span style="font-size: 12px; font-weight: 500; color: #FFF; word-break: break-word; overflow-wrap: break-word;">${
                point.y?.toFixed(1) ?? "0.0"
                } ${yAxisUnit}</span>
                        </div>`;
        });

        html += `
                    </div>
                </div>
                
            </div>
        `;

        html += `
            <div style="margin-bottom: 12px; padding-bottom: 12px; border-bottom: 1px solid #414651;"></div>
        `;

        tooltipMetrics.forEach((metric: any, index: number) => {
            const value = tooltipData[metric.key];
            if (value !== undefined) {
                const displayValue =
                    metric.unit === currencySymbol
                        ? `${metric.unit} ${value?.toFixed(0) ?? "0"}`
                        : metric.unit === ""
                            ? `${value?.toFixed(1) ?? "0.0"}`
                            : `${value?.toFixed(1) ?? "0.0"}${metric.unit}`;

                html += `
                    <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: ${
                    index < tooltipMetrics.length - 1 ? "2px" : "0"
                    };">
                        <span style="color: #E0E0E0; font-size: 12px; font-weight: 600;">${
                    metric.label
                        }</span>
                        <span style="color: #FFF; font-size: 12px; font-weight: 600;">${displayValue}</span>
                    </div>
                `;
            }
        });
    } else {
        // Show loading when no product data is present
        html += htmlLoader
    }

    html += `</div>`;
    return html;
}

export function skuCDTooltipFormatter(
    this: Highcharts.TooltipFormatterContextObject,
    currencySymbol: string
): string {
    const chart = (this as any).points?.[0]?.series?.chart;
    if (!chart) return "";

    // Get all points at the current x position across all series
    const currentX = (this as any).points?.[0]?.point?.x;
    const allPoints: any[] = [];

    if (currentX !== undefined && chart.series) {
        chart.series.forEach((series: any) => {
            if (
                series.visible &&
                series.points &&
                series.points[currentX]
            ) {
                allPoints.push(series.points[currentX]);
            }
        });
    }

    // Fallback to this.points if we couldn't get all points
    const points =
        allPoints.length > 0 ? allPoints : (this as any).points || [];

    const tooltipDataArray = chart.userOptions.tooltipData || [];
    const tooltipData = tooltipDataArray[0] || {};
    const tooltipMetrics = chart.userOptions.tooltipMetrics || [];
    const showTooltipPoints = chart.userOptions.showTooltipPoints ?? false;
    const yAxisUnit = chart.userOptions.yAxisUnit || "%";

    const hasData = tooltipData.channel_id;

    let html = `<div style="padding: ${hasData ? "16px" : "0"
        }; border-radius: 12px; background: #1E1B39; width: 218px; min-height: 150px; box-shadow: 0 4px 12px rgba(0,0,0,0.3);">`;

    if (hasData) {
        // Get channel name
        const channelId = Number(tooltipData.channel_id);
        const channelName = channelsMapping[channelId] || "Unknown";
        const displayChannelName = channelName.charAt(0).toUpperCase() + channelName.slice(1);

        // Header with channel name
        html += `
            <div style="margin-bottom: 5px; text-align: left;">
                <div style="color: #00A8E8; font-size: 14px; font-weight: 600; margin-bottom: 8px;">${displayChannelName}</div>
        `;

        // Conditionally render points if showPoints is true
        if (showTooltipPoints && points.length > 0) {
            html += `
                <div style="display: flex; gap: 16px; margin-bottom: 8px;">`;

            points.forEach((point: any) => {
                html += `
                    <div style="display: flex; align-items: center; gap: 6px;">
                        <span style="display: inline-block; width: 8px; height: 8px; border-radius: 50%; background: ${
                    point.color
                        };"></span>
                        <span style="font-size: 12px; font-weight: 500; color: #FFF;">${
                    point.y?.toFixed(1) ?? "0.0"
                    } ${yAxisUnit}</span>
                    </div>`;
            });

            html += `
                </div>`;

            // Add divider below points
            html += `
                <div style="margin: 12px 0; border-bottom: 1px solid #414651;"></div>`;
        }

        html += `
            </div>
        `;

        tooltipMetrics.forEach((metric: any, index: number) => {
            if (metric.type === "divider") {
                html += `
                    <div style="margin: 12px 0; border-bottom: 1px solid #414651;"></div>
                `;
            } else {
                const value = tooltipData[metric.key];
                if (value !== undefined && value !== null) {
                    const displayValue =
                        metric.unit === currencySymbol
                            ? `${metric.unit} ${value?.toFixed(0) ?? "0"}`
                            : metric.unit === "%"
                                ? `${value?.toFixed(1) ?? "0.0"}${metric.unit}`
                                : `${value?.toFixed(0) ?? "0"}`;

                    const nextItem = tooltipMetrics[index + 1];
                    const isBeforeDivider = nextItem?.type === "divider";
                    const isLastItem = index === tooltipMetrics.length - 1;

                    html += `
                        <div style="display: flex; justify-content: space-between; align-items: center; margin-bottom: ${
                        isBeforeDivider || isLastItem ? "0" : "5px"
                        };">
                            <span style="color: #E0E0E0; font-size: 12px; font-weight: 600;">${
                        metric.label
                            }</span>
                            <span style="color: #FFF; font-size: 12px; font-weight: 600;">${displayValue}</span>
                        </div>
                    `;
                }
            }
        });
    } else {
        // Show loading when no channel data is present
        html += htmlLoader;
    }

    html += `</div>`;
    return html;
}

export const trendsChartConfig = (filterToAppend: string) => {
    return {
        widgetCard: {
            type: "drilldown",
            isBodyPaddingReq: true,
            bodyPaddingClass: "p-0"
        },
        isChartLoadingRequired: true,
        chartDimensions: {
            minHeight: 100,
            maxHeight: 370,
            // The heightPercent controls the chart's height in the above sepcified min and max height,
            // taking the viewPort height into account
            heightPercent: 0.4
        },
        chartType: 'areaspline',
        isCustomLegendRequired: false,
        isChartActionsRequired: false,
        isTimePeriodSelectionRequired: false,
        isYAxisMeasuresSelectionRequired: false,
        isOppositeMetricSelectionRequired: false,
        isGranularitySelectionRequired: false,
        defaultGranularity: "month",
        isAdvanceTrendsBtnRequired: false,
        enableAdvanceTrendsBtn: true,
        hasChartFilters: false,
        hasFilterDropDown: false,
        isRemovablePillsRequired: false,
        isInternalFilterRequired: false,
        capitalizePillNames: false,
        defaultSelectedOppositeMetric: capitalizeSpecificSubstring(titleCase(CommonWidgetMetrics.OVERALL_SOV), "sov"),
        defaultSelectedMetric: `market_share${filterToAppend}`,
        granularityOptions: [
            { label: "Daily", value: RevstackFrequencySlicerOptions.Daily },
            { label: "Weekly", value: RevstackFrequencySlicerOptions.Weekly },
            { label: "Monthly", value: RevstackFrequencySlicerOptions.Monthly },
        ],
        defaultSelectedGranularity: RevstackFrequencySlicerOptions.Weekly,
        graphAttributes: {
            yAxisTitle: "Market Share in %",
            min: 0,
            height: 400,
            hideXAxisTitle: true,
            xAxis: {
                tickmarkPlacement: "on",
                labels: {
                    enabled: false
                },
                crosshair: {
                    width: 2,
                    color: "#605BFF",
                    dashStyle: "Dot",
                    zIndex: 5,
                },
            },
            tooltip: {
                shared: false,
                useHTML: true,
                backgroundColor: "#030229",
                borderRadius: 25,
                borderWidth: 0,
                formatter: function (
                    this: Highcharts.TooltipFormatterContextObject
                ): string {
                    const unit =
                        (this.series as any)
                            .userOptions.unit || "";

                    // Get the date from the x-axis category
                    const date = this.x !== undefined ? this.x : "";
                    const dateDisplay = typeof date === 'string' ? date : String(date);
                    return `
                <div style="text-align:center;">
                    <div style="font-size:10px; font-weight:600; margin-bottom:4px; color: #ccc; font-style:normal; font-family: 'Manrope', sans-serif;">${dateDisplay}</div>
                    <div style="font-size:12px; font-weight:600; margin-bottom:2px; color: #fff; font-style:normal; font-family: 'Manrope', sans-serif;">${this.series.name}</div>
                    <div style="font-size:16px; font-weight:600; font-family: 'Nunito', sans-serif; color: #fff;">
                    ${this.y?.toFixed(1)} ${unit}
                    </div>
                </div>
                `;
                },
            },
            plotOptions: {
                series: {
                    marker: { enabled: false },
                    lineWidth: 1.5,
                    pointPlacement: "on",
                },
            },
            minorTicksLabelLimit: 3
        },
        graphColors: REVSTACK_TRENDS_LIGHT_CHART_COLORS,
        maxPillsFiltersAllowed: 5,
        yAxisOppoToolTipText: "Comparison is available for single selection only",
        dateFormat: "D/MM/YY",
        minDatelabels: 14
    }

}

export const chartLabelToolTooltipFormatter = (fullLabel: string, truncatedLabel: string) => {
    // Using native browser tooltip via title attribute
    return `
        <span class="custom-chart-label-tooltip" 
              title="${fullLabel}"
              style="position: relative; cursor: pointer;">
            ${truncatedLabel}
        </span>
    `;
}